import { toError } from "@clipboard-health/util-ts";
import { HyperTrack, type LocationError } from "hypertrack-sdk-ionic-capacitor";

export async function getUserCurrentLocation(): Promise<{ latitude?: number; longitude?: number }> {
  const location = await HyperTrack.getLocation();
  if (location.type === "success") {
    return {
      latitude: location.value.latitude,
      longitude: location.value.longitude,
    };
  }

  return {};
}

interface GeofenceRequest {
  shiftId: string;
  workerId: string;
}
interface GeofenceResult {
  isInsideGeofence: boolean;
  error?: string | Error;
}

/**
 * Check if the worker is within the Shift's geofence.
 *
 * The reason why it is a Shift and not Workplace is for clarity.
 * Updating the Workplace's geofence while the order is active does not update the order's geofence.
 *
 * This function expects at least one order to exist for the worker, that's why we return an error if no orders are found.
 * It's up to the caller to determine whether there was an order at all. For example, some workplace types don't allow tracking orders.
 */
export async function isWorkerWithinShiftGeofence(
  params: GeofenceRequest
): Promise<GeofenceResult> {
  const { shiftId, workerId } = params;

  try {
    const orders = await HyperTrack.getOrders();
    if (orders.size === 0) {
      return {
        isInsideGeofence: false,
        error: `No orders found for worker ${workerId}, shift: ${shiftId}`,
      };
    }

    const orderHandle = orderHandleForShift({ shiftId, workerId });
    const order = orders.get(orderHandle);
    if (!order) {
      return {
        isInsideGeofence: false,
        error: `Order not found in HyperTrack, orderHandle: ${orderHandle}`,
      };
    }

    const isInsideGeofence = await order.isInsideGeofence();
    if (isInsideGeofence.type !== "success") {
      const error = getLocationErrorType(isInsideGeofence.value);
      return {
        isInsideGeofence: false,
        error: `HyperTrack error: ${error}, orderHandle: ${orderHandle}`,
      };
    }

    return { isInsideGeofence: isInsideGeofence.value, error: undefined };
  } catch (error) {
    return { isInsideGeofence: false, error: toError(error) };
  }
}

export function orderHandleForShift({ shiftId, workerId }: { shiftId: string; workerId: string }) {
  return `${shiftId}-${workerId}`;
}

function getLocationErrorType(error: LocationError): string {
  switch (error.type) {
    case "notRunning":
    case "starting": {
      return error.type;
    }

    case "errors": {
      return error.value.join(", ");
    }

    default: {
      return "Unknown HyperTrack error";
    }
  }
}
